/**
 * CumulativeReturnComparison Component
 *
 * Dual-line chart showing pre-tax vs after-tax cumulative return over 10 years.
 * Visually highlights the bonus depreciation (OBBB) impact as the gap between lines.
 */

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { formatCurrency, formatCurrencyCompact } from '../../utils/formatters';
import type { YearProjection } from '../../types/calculations';
import { SectionTitle } from '../Luxury';

interface CumulativeReturnComparisonProps {
  projections: YearProjection[];
  equityInvested: number;
}

export function CumulativeReturnComparison({
  projections,
  equityInvested: _equityInvested,
}: CumulativeReturnComparisonProps) {
  const operatingYears = projections.filter(p => p.year > 0);

  // Need both cumulative fields to show comparison
  const hasAfterTax = operatingYears.some(p => p.after_tax_cumulative_cf != null);
  const hasPreTax = operatingYears.some(p => p.cumulative_cash_flow != null);
  if (!hasAfterTax || !hasPreTax) return null;

  const chartData = operatingYears.map(p => ({
    year: `Year ${p.year}`,
    preTax: p.cumulative_cash_flow ?? 0,
    afterTax: p.after_tax_cumulative_cf ?? p.cumulative_cash_flow ?? 0,
  }));

  // Calculate key metrics for the summary
  const year1 = operatingYears.find(p => p.year === 1);
  const year5 = operatingYears.find(p => p.year === 5);
  const year10 = operatingYears.find(p => p.year === 10);

  const year1Delta = (year1?.after_tax_cumulative_cf ?? 0) - (year1?.cumulative_cash_flow ?? 0);
  const year5Delta = (year5?.after_tax_cumulative_cf ?? 0) - (year5?.cumulative_cash_flow ?? 0);
  const year10Delta = (year10?.after_tax_cumulative_cf ?? 0) - (year10?.cumulative_cash_flow ?? 0);

  // Year 1 bonus depreciation tax benefit (biggest single-year impact)
  const year1TaxBenefit = year1?.tax_benefit ?? 0;
  const year1BonusDepr = year1?.bonus_depreciation ?? 0;

  // Breakeven analysis: when does after-tax cumulative turn positive?
  const breakevenYear = operatingYears.find(p => (p.after_tax_cumulative_cf ?? 0) >= 0)?.year;

  return (
    <div className="rounded-xl border border-slate-dark bg-charcoal p-6 space-y-5">
      <div>
        <SectionTitle>Cumulative Return: Pre-Tax vs After-Tax (OBBB)</SectionTitle>
        <p className="text-sm text-stone-700 mt-1">
          The gap between lines represents total depreciation tax savings — dominated by Year 1 bonus depreciation under OBBB.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-graphite rounded-lg border border-slate-dark p-3 text-center">
          <p className="text-sm text-stone-700 uppercase tracking-wider mb-1">Year 1 Tax Benefit</p>
          <p className="text-lg font-mono font-bold text-amber-400">
            {formatCurrency(year1TaxBenefit)}
          </p>
          {year1BonusDepr > 0 && (
            <p className="text-sm text-stone-700 mt-0.5">
              incl. {formatCurrency(year1BonusDepr)} bonus depr.
            </p>
          )}
        </div>
        <div className="bg-graphite rounded-lg border border-slate-dark p-3 text-center">
          <p className="text-sm text-stone-700 uppercase tracking-wider mb-1">5yr Tax Impact</p>
          <p className="text-lg font-mono font-bold text-emerald">
            +{formatCurrency(year5Delta)}
          </p>
          <p className="text-sm text-stone-700 mt-0.5">added to cumulative return</p>
        </div>
        <div className="bg-graphite rounded-lg border border-slate-dark p-3 text-center">
          <p className="text-sm text-stone-700 uppercase tracking-wider mb-1">10yr Tax Impact</p>
          <p className="text-lg font-mono font-bold text-emerald">
            +{formatCurrency(year10Delta)}
          </p>
          {breakevenYear && (
            <p className="text-sm text-stone-700 mt-0.5">
              Breakeven: Year {breakevenYear}
            </p>
          )}
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#d4d1cc" opacity={0.15} />
          <XAxis
            dataKey="year"
            stroke="#d4d1cc"
            style={{ fontSize: '11px', fontFamily: 'JetBrains Mono, monospace' }}
          />
          <YAxis
            stroke="#d4d1cc"
            style={{ fontSize: '11px', fontFamily: 'JetBrains Mono, monospace' }}
            width={70}
            tickFormatter={(v) => formatCurrencyCompact(v)}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '8px',
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: '12px',
            }}
            labelStyle={{ color: '#d4a853', fontWeight: 600 }}
            formatter={(value: number, name: string) => [
              formatCurrency(value),
              name === 'preTax' ? 'Pre-Tax Cumulative' : 'After-Tax Cumulative (OBBB)',
            ]}
          />
          <Legend
            wrapperStyle={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
            formatter={(value) =>
              value === 'preTax' ? 'Pre-Tax Cumulative' : 'After-Tax Cumulative (OBBB)'
            }
          />

          {/* Zero reference line */}
          <ReferenceLine y={0} stroke="#78716c" strokeDasharray="3 3" opacity={0.5} />

          {/* Pre-tax cumulative - dashed stone line */}
          <Line
            type="monotone"
            dataKey="preTax"
            stroke="#78716c"
            strokeDasharray="6 4"
            strokeWidth={2}
            name="preTax"
            dot={false}
          />

          {/* After-tax cumulative - solid amber line with dots */}
          <Line
            type="monotone"
            dataKey="afterTax"
            stroke="#d4a853"
            strokeWidth={3}
            name="afterTax"
            dot={{ fill: '#d4a853', r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Explanation */}
      <div className="bg-obsidian/50 rounded-lg border border-amber-500/20 p-3">
        <p className="text-sm text-stone-700 leading-relaxed">
          <span className="text-amber-400 font-semibold">OBBB 2025-2026:</span>{' '}
          100% first-year bonus depreciation on qualifying CapEx (90% eligible: kitchens, baths, flooring, MEP).
          The Year 1 spike in the after-tax line reflects the {formatCurrency(year1BonusDepr)} bonus deduction
          generating {formatCurrency(year1TaxBenefit)} in tax savings at the 40% effective rate.
          Years 2-10 continue building with {formatCurrency((year10Delta - year1Delta) / 9)}/yr
          in standard building depreciation benefits.
        </p>
      </div>
    </div>
  );
}

export default CumulativeReturnComparison;
